import React, { useEffect, useState } from "react";
import axiosInstance from "../../../axios";
import { useSelector } from "react-redux";
import moment from "moment/moment";
import { FaBell, FaCalendarAlt, FaBullhorn, FaCheckCircle, FaTrashAlt, FaSpinner, FaInfoCircle, FaRegClock, FaExclamationCircle } from "react-icons/fa";
import { Loading } from "../../Other/Loading";

export default function RemindersPage() {
  const { user } = useSelector((state) => state.users);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("all");
  
  // Real data state
  const [webinars, setWebinars] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  
  // Custom custom task list
  const [tasks, setTasks] = useState([
    {
      id: "t1",
      title: "Complete Python basics Assignment 3",
      course: "Intro to Python Programming",
      dueDate: moment().add(1, "days").format("YYYY-MM-DD"),
      type: "assignment",
      priority: "high",
      status: "pending"
    },
    {
      id: "t2",
      title: "Attempt Quiz 2 on Advanced SQL",
      course: "Relational Databases & SQL",
      dueDate: moment().add(3, "days").format("YYYY-MM-DD"),
      type: "quiz",
      priority: "medium",
      status: "pending"
    },
    {
      id: "t3",
      title: "Submit final capstone project draft",
      course: "Data Science Specialization",
      dueDate: moment().add(5, "days").format("YYYY-MM-DD"),
      type: "project",
      priority: "high",
      status: "pending"
    }
  ]);

  const [notificationSettings, setNotificationSettings] = useState({
    email: true,
    browser: true,
    sms: false
  });

  const now = new Date();
  const firstDay = moment(new Date(now.getFullYear(), now.getMonth(), 1)).format("YYYY MM,DD");
  const lastDay = moment(new Date(now.getFullYear(), now.getMonth() + 1, 0)).format("YYYY MM,DD");

  useEffect(() => {
    const fetchRemindersData = async () => {
      try {
        setLoading(true);
        // Fetch real webinars
        const webinarRes = await axiosInstance.get(`webinar/all?startDate=${firstDay}&endDate=${lastDay}`);
        if (webinarRes?.data?.data) {
          setWebinars(webinarRes.data.data);
        }
        
        // Fetch real announcements
        const announcementRes = await axiosInstance.get("/announcement/");
        if (announcementRes?.data?.data) {
          setAnnouncements(announcementRes.data.data);
        }
      } catch (err) {
        console.error("Error fetching reminders data:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchRemindersData();
  }, []);

  const handleMarkTaskComplete = (taskId) => {
    setTasks(tasks.map(t => t.id === taskId ? { ...t, status: t.status === "completed" ? "pending" : "completed" } : t));
  };

  const handleDeleteTask = (taskId) => {
    setTasks(tasks.filter(t => t.id !== taskId));
  };

  const handleToggleSetting = (key) => {
    setNotificationSettings(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // Process items into unified reminder list
  const unifiedReminders = [];

  // 1. Process webinars
  webinars.forEach((web) => {
    const diffDays = moment(web.webinarDate).diff(moment(now), "days");
    let status = "upcoming";
    let statusText = `${diffDays} Days Left`;
    let urgency = "medium";

    if (diffDays === 0) {
      status = "live";
      statusText = "Happening Today!";
      urgency = "high";
    } else if (diffDays < 0) {
      status = "past";
      statusText = "Ended";
      urgency = "low";
    } else if (diffDays === 1) {
      statusText = "Tomorrow";
      urgency = "high";
    }

    unifiedReminders.push({
      id: `web_${web.id}`,
      title: web.eventName,
      subtitle: `Webinar Class • Presented by Instructor`,
      date: web.webinarDate,
      time: `${web.startTime} - ${web.endTime || "N/A"}`,
      type: "webinar",
      badgeText: statusText,
      urgency,
      status,
      link: web.link || "#"
    });
  });

  // 2. Process announcements
  announcements.forEach((ann, idx) => {
    unifiedReminders.push({
      id: `ann_${idx}`,
      title: ann.name,
      subtitle: "System Announcement",
      date: ann.lastUpdate,
      time: ann.time,
      type: "announcement",
      badgeText: "New",
      urgency: "low",
      status: "new"
    });
  });

  // 3. Process custom tasks
  tasks.forEach((task) => {
    const diffDays = moment(task.dueDate).diff(moment(now), "days");
    let urgency = "medium";
    let badgeText = `${diffDays} Days Left`;

    if (diffDays === 0) {
      urgency = "high";
      badgeText = "Due Today!";
    } else if (diffDays === 1) {
      urgency = "high";
      badgeText = "Due Tomorrow";
    } else if (diffDays < 0) {
      urgency = "low";
      badgeText = "Overdue";
    }

    unifiedReminders.push({
      id: task.id,
      title: task.title,
      subtitle: `${task.course} • ${task.type.toUpperCase()}`,
      date: task.dueDate,
      type: "task",
      badgeText,
      urgency: task.status === "completed" ? "low" : urgency,
      status: task.status,
      originalTask: task
    });
  });

  // Filter reminders based on active tab
  const filteredReminders = unifiedReminders.filter((rem) => {
    if (activeTab === "all") return true;
    return rem.type === activeTab;
  });

  // Sort: High urgency first, then date
  filteredReminders.sort((a, b) => {
    const urgencyWeight = { high: 3, medium: 2, low: 1 };
    const weightDiff = (urgencyWeight[b.urgency] || 0) - (urgencyWeight[a.urgency] || 0);
    if (weightDiff !== 0) return weightDiff;
    return moment(a.date).valueOf() - moment(b.date).valueOf();
  });

  return (
    <div className="container-wrapper p-6 bg-slate-50/50 min-h-screen">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between mb-8 pb-5 border-b border-slate-200">
        <div>
          <h1 className="text-3xl font-extrabold text-slate-800 flex items-center gap-3 tracking-tight">
            <span className="p-3 bg-gradient-to-tr from-rose-500 to-red-600 rounded-2xl text-white shadow-lg shadow-rose-200">
              <FaBell className="animate-bounce" size={22} />
            </span>
            Personal Reminders & Tasks
          </h1>
          <p className="text-slate-500 mt-2 font-medium">
            Stay on top of your learning commitments, upcoming webinars, tasks, and system announcements.
          </p>
        </div>
        
        {/* Real-time stats */}
        <div className="mt-4 lg:mt-0 flex gap-4">
          <div className="bg-white px-5 py-3 rounded-2xl border border-slate-200 shadow-sm flex items-center gap-3">
            <span className="w-3.5 h-3.5 bg-rose-500 rounded-full animate-ping"></span>
            <div>
              <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">Unresolved</p>
              <p className="text-lg font-black text-slate-800">{filteredReminders.filter(r => r.status !== "completed" && r.status !== "past").length}</p>
            </div>
          </div>
          <div className="bg-white px-5 py-3 rounded-2xl border border-slate-200 shadow-sm flex items-center gap-3">
            <span className="w-3.5 h-3.5 bg-emerald-500 rounded-full"></span>
            <div>
              <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">Completed</p>
              <p className="text-lg font-black text-slate-800">{tasks.filter(t => t.status === "completed").length}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
        {/* Main Reminders Column */}
        <div className="xl:col-span-2 flex flex-col gap-6">
          {/* Tabs Filter */}
          <div className="bg-white p-2 rounded-2xl border border-slate-200 shadow-sm flex gap-1">
            {[
              { id: "all", label: "All Reminders", count: unifiedReminders.length },
              { id: "webinar", label: "Webinars & Classes", count: webinars.length },
              { id: "task", label: "Tasks & Deadlines", count: tasks.length },
              { id: "announcement", label: "Announcements", count: announcements.length }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-1 py-2.5 px-3 rounded-xl font-bold text-sm transition-all flex items-center justify-center gap-2 ${
                  activeTab === tab.id
                    ? "bg-slate-800 text-white shadow-md shadow-slate-900/10"
                    : "text-slate-500 hover:text-slate-800 hover:bg-slate-50"
                }`}
              >
                {tab.label}
                <span className={`text-[11px] px-2 py-0.5 rounded-full ${
                  activeTab === tab.id ? "bg-white/20 text-white" : "bg-slate-100 text-slate-600"
                }`}>
                  {tab.count}
                </span>
              </button>
            ))}
          </div>

          {/* List Section */}
          {loading ? (
            <div className="bg-white p-16 rounded-3xl border border-slate-200 shadow-sm flex flex-col items-center justify-center gap-4">
              <FaSpinner className="animate-spin text-slate-400" size={36} />
              <p className="text-slate-500 font-bold">Synchronizing reminders and tasks...</p>
            </div>
          ) : filteredReminders.length === 0 ? (
            <div className="bg-white p-16 rounded-3xl border border-slate-200 shadow-sm text-center">
              <span className="text-4xl">🎉</span>
              <h3 className="text-xl font-bold text-slate-800 mt-4">All Clear!</h3>
              <p className="text-slate-500 mt-1 max-w-sm mx-auto font-medium">
                No reminders found for this category. You are fully caught up with all tasks and webinars!
              </p>
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              {filteredReminders.map((reminder) => {
                const isTask = reminder.type === "task";
                const isWebinar = reminder.type === "webinar";
                const isAnnouncement = reminder.type === "announcement";
                const isCompleted = reminder.status === "completed";

                // Urgency styles
                let urgencyClass = "border-slate-100 hover:border-slate-300";
                let urgencyBadge = "bg-slate-100 text-slate-600";
                
                if (!isCompleted) {
                  if (reminder.urgency === "high") {
                    urgencyClass = "border-l-4 border-l-rose-500 border-y-slate-200 border-r-slate-200 bg-rose-50/10 hover:bg-rose-50/20";
                    urgencyBadge = "bg-rose-100 text-rose-700 animate-pulse";
                  } else if (reminder.urgency === "medium") {
                    urgencyClass = "border-l-4 border-l-amber-500 border-y-slate-200 border-r-slate-200 bg-amber-50/10 hover:bg-amber-50/20";
                    urgencyBadge = "bg-amber-100 text-amber-700";
                  }
                }

                return (
                  <div
                    key={reminder.id}
                    className={`bg-white p-5 rounded-2xl border shadow-sm transition-all duration-200 hover:shadow-md ${urgencyClass} ${
                      isCompleted ? "opacity-60" : ""
                    }`}
                  >
                    <div className="flex items-start gap-4">
                      {/* Interactive checkbox for tasks */}
                      {isTask ? (
                        <button
                          onClick={() => handleMarkTaskComplete(reminder.id)}
                          className="mt-1 shrink-0 text-slate-300 hover:text-emerald-500 transition-colors"
                          title="Toggle Completion Status"
                        >
                          <FaCheckCircle
                            size={22}
                            className={isCompleted ? "text-emerald-500" : "text-slate-200"}
                          />
                        </button>
                      ) : (
                        <div className="mt-1 shrink-0 p-2.5 rounded-xl bg-slate-50 text-slate-500">
                          {isWebinar ? <FaCalendarAlt size={16} className="text-indigo-500" /> : <FaBullhorn size={16} className="text-purple-500" />}
                        </div>
                      )}

                      {/* Content block */}
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2 mb-1.5">
                          <span className={`text-xs font-extrabold uppercase px-2.5 py-0.5 rounded-full ${urgencyBadge}`}>
                            {reminder.badgeText}
                          </span>
                          <span className="text-[11px] font-bold text-slate-400 flex items-center gap-1.5 ml-auto">
                            <FaRegClock size={11} />
                            {moment(reminder.date).format("MMM DD, YYYY")} {reminder.time ? `• ${reminder.time}` : ""}
                          </span>
                        </div>
                        
                        <h4 className={`text-base font-extrabold text-slate-800 tracking-tight truncate ${
                          isCompleted ? "line-through text-slate-400" : ""
                        }`}>
                          {reminder.title}
                        </h4>
                        
                        <p className="text-sm font-medium text-slate-500 mt-1">
                          {reminder.subtitle}
                        </p>
                      </div>

                      {/* Actions */}
                      <div className="shrink-0 flex items-center gap-2 self-center">
                        {isWebinar && reminder.status !== "past" && (
                          <button
                            onClick={() => reminder.link !== "#" ? window.open(reminder.link, "_blank") : alert("Joining link is not available yet!")}
                            className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-xl text-xs font-black tracking-wider transition-all shadow-md shadow-indigo-200 uppercase"
                          >
                            Join Class
                          </button>
                        )}
                        {isTask && (
                          <button
                            onClick={() => handleDeleteTask(reminder.id)}
                            className="p-2.5 text-slate-400 hover:text-rose-500 hover:bg-rose-50 rounded-xl transition-all"
                            title="Delete Task"
                          >
                            <FaTrashAlt size={14} />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Right Sidebar Column - Controls & Preferences */}
        <div className="flex flex-col gap-6">
          {/* Quick Task Creation Card */}
          <div className="bg-white p-6 rounded-3xl border border-slate-200 shadow-sm">
            <h3 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
              <span className="w-2.5 h-6 bg-rose-500 rounded-full"></span>
              Create Quick Reminder
            </h3>
            
            <form onSubmit={(e) => {
              e.preventDefault();
              const fd = new FormData(e.target);
              const title = fd.get("title");
              const course = fd.get("course");
              const date = fd.get("date");
              const priority = fd.get("priority");
              
              if (!title || !date) return alert("Please fill out Title and Due Date!");
              
              const newTask = {
                id: `t_${Date.now()}`,
                title: String(title),
                course: course ? String(course) : "General Reminder",
                dueDate: String(date),
                type: "task",
                priority: String(priority),
                status: "pending"
              };
              
              setTasks([newTask, ...tasks]);
              e.target.reset();
            }} className="flex flex-col gap-3.5">
              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider block mb-1.5">Reminder Title</label>
                <input
                  type="text"
                  name="title"
                  placeholder="e.g. Finish chemistry project"
                  className="w-full border border-slate-200 px-3.5 py-2.5 rounded-xl text-sm font-semibold text-slate-800 placeholder-slate-400 focus:outline-none focus:border-rose-500 bg-slate-50/50"
                  required
                />
              </div>

              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider block mb-1.5">Associated Course / Tag</label>
                <input
                  type="text"
                  name="course"
                  placeholder="e.g. Chemistry 101"
                  className="w-full border border-slate-200 px-3.5 py-2.5 rounded-xl text-sm font-semibold text-slate-800 placeholder-slate-400 focus:outline-none focus:border-rose-500 bg-slate-50/50"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-wider block mb-1.5">Due Date</label>
                  <input
                    type="date"
                    name="date"
                    className="w-full border border-slate-200 px-3 py-2 rounded-xl text-sm font-semibold text-slate-800 focus:outline-none focus:border-rose-500 bg-slate-50/50"
                    required
                  />
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-wider block mb-1.5">Priority</label>
                  <select
                    name="priority"
                    className="w-full border border-slate-200 px-3 py-2.5 rounded-xl text-sm font-semibold text-slate-800 focus:outline-none focus:border-rose-500 bg-slate-50/50"
                  >
                    <option value="high">🔥 High</option>
                    <option value="medium">⚡ Medium</option>
                    <option value="low">💤 Low</option>
                  </select>
                </div>
              </div>

              <button
                type="submit"
                className="w-full bg-slate-800 hover:bg-slate-900 text-white font-extrabold py-3 rounded-xl text-xs uppercase tracking-wider shadow-md transition-all mt-2"
              >
                Add Reminder
              </button>
            </form>
          </div>

          {/* Preferences Card */}
          <div className="bg-white p-6 rounded-3xl border border-slate-200 shadow-sm">
            <h3 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
              <span className="w-2.5 h-6 bg-indigo-500 rounded-full"></span>
              Notification Channels
            </h3>
            
            <div className="flex flex-col gap-4">
              <div className="flex items-center justify-between border-b border-slate-100 pb-3">
                <div>
                  <p className="text-sm font-bold text-slate-800">Email Notifications</p>
                  <p className="text-xs text-slate-400 font-medium">Daily digest of upcoming items</p>
                </div>
                <button
                  onClick={() => handleToggleSetting("email")}
                  className={`w-11 h-6 rounded-full transition-all relative ${
                    notificationSettings.email ? "bg-emerald-500" : "bg-slate-200"
                  }`}
                >
                  <span className={`w-4 h-4 bg-white rounded-full absolute top-1 transition-all ${
                    notificationSettings.email ? "right-1" : "left-1"
                  }`}></span>
                </button>
              </div>

              <div className="flex items-center justify-between border-b border-slate-100 pb-3">
                <div>
                  <p className="text-sm font-bold text-slate-800">Browser Alerts</p>
                  <p className="text-xs text-slate-400 font-medium">Push notifications for live webinars</p>
                </div>
                <button
                  onClick={() => handleToggleSetting("browser")}
                  className={`w-11 h-6 rounded-full transition-all relative ${
                    notificationSettings.browser ? "bg-emerald-500" : "bg-slate-200"
                  }`}
                >
                  <span className={`w-4 h-4 bg-white rounded-full absolute top-1 transition-all ${
                    notificationSettings.browser ? "right-1" : "left-1"
                  }`}></span>
                </button>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-bold text-slate-800">SMS Reminders</p>
                  <p className="text-xs text-slate-400 font-medium">Mobile alerts for urgent tasks</p>
                </div>
                <button
                  onClick={() => handleToggleSetting("sms")}
                  className={`w-11 h-6 rounded-full transition-all relative ${
                    notificationSettings.sms ? "bg-emerald-500" : "bg-slate-200"
                  }`}
                >
                  <span className={`w-4 h-4 bg-white rounded-full absolute top-1 transition-all ${
                    notificationSettings.sms ? "right-1" : "left-1"
                  }`}></span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
